import { Link } from "react-router-dom";
import { useEffect } from "react";
import { Shield, MapPin, Star, Car, Coffee, Castle, MountainSnow, RollerCoaster, Utensils, ShoppingBag, Award, BadgeCheck, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";

export const GraphicsSupplyFitting = () => {
  // Scroll to top when page loads
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="pt-24 pb-16" data-testid="graphics-supply-fitting-page">
      <div className="max-w-4xl mx-auto px-4 md:px-8">
        {/* Back Button */}
        <Link to="/" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-8 transition-colors">
          <ArrowLeft className="h-4 w-4" />
          <span>Back to Home</span>
        </Link>

        {/* Page Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-black rounded-full mb-6">
            <BadgeCheck className="h-10 w-10 text-white" />
          </div>
          <h1 
            className="text-3xl md:text-4xl lg:text-5xl font-normal tracking-tight mb-4"
            style={{ fontFamily: 'Calibri, Carlito, "Segoe UI", sans-serif' }}
          >
            Professional Car Graphics Supply & Fitting
          </h1>
          <div className="flex items-center justify-center gap-2 text-muted-foreground mb-4">
            <MapPin className="h-5 w-5" />
            <span className="text-lg">Tamworth, Midlands</span>
          </div>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            We supply and fit high-quality car graphics, stripes, and decals, offering a full professional fitting service right here in Tamworth.
          </p>
        </div>

        {/* Main Features Section */}
        <div className="mb-12">
          <div className="bg-gray-100 rounded-3xl p-8 md:p-10" data-testid="features-section">
            <div className="grid md:grid-cols-3 gap-8 mb-8">
              {/* 5-Star Facility */}
              <div className="flex flex-col items-center text-center p-6 bg-white rounded-2xl shadow-sm">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                  <Star className="h-8 w-8 text-gray-700" />
                </div>
                <h3 className="font-bold text-lg mb-3" style={{ fontFamily: 'Calibri, Carlito, "Segoe UI", sans-serif' }}>
                  5-Star Facility
                </h3>
                <p className="text-muted-foreground">
                  Air-conditioned work bay, clean & modern environment for premium results
                </p>
              </div>

              {/* Expert Fitting */}
              <div className="flex flex-col items-center text-center p-6 bg-white rounded-2xl shadow-sm">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                  <Car className="h-8 w-8 text-gray-700" />
                </div>
                <h3 className="font-bold text-lg mb-3" style={{ fontFamily: 'Calibri, Carlito, "Segoe UI", sans-serif' }}>
                  Expert Fitting
                </h3>
                <p className="text-muted-foreground">
                  Professional installation from start to finish by experienced specialists
                </p>
              </div>

              {/* Convenient Location */}
              <div className="flex flex-col items-center text-center p-6 bg-white rounded-2xl shadow-sm">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                  <Coffee className="h-8 w-8 text-gray-700" />
                </div>
                <h3 className="font-bold text-lg mb-3" style={{ fontFamily: 'Calibri, Carlito, "Segoe UI", sans-serif' }}>
                  Convenient Location
                </h3>
                <p className="text-muted-foreground">
                  Minutes from Tamworth's shops, cafés & attractions
                </p>
              </div>
            </div>

            <div className="text-center mb-8">
              <div className="flex items-center justify-center gap-4 mb-4">
                <Castle className="h-8 w-8 text-muted-foreground" />
                <MountainSnow className="h-8 w-8 text-muted-foreground" />
                <RollerCoaster className="h-8 w-8 text-muted-foreground" />
                <Utensils className="h-8 w-8 text-muted-foreground" />
                <ShoppingBag className="h-8 w-8 text-muted-foreground" />
              </div>
              <p 
                className="text-muted-foreground text-xl italic"
                style={{ fontFamily: 'Calibri, Carlito, "Segoe UI", sans-serif' }}
              >
                Plenty to see and do while your graphics are expertly fitted!
              </p>
            </div>

            <div className="text-center">
              <Link to="/contact">
                <Button 
                  className="rounded-full px-10 py-4 text-lg font-medium"
                  data-testid="book-appointment-btn"
                >
                  Book an Appointment
                </Button>
              </Link>
            </div>
          </div>
        </div>

        {/* Sytner Approved Supplier Section */}
        <div className="mb-8">
          <div className="bg-gray-100 rounded-3xl p-8" data-testid="sytner-section">
            <div className="flex items-center justify-center gap-3 mb-6">
              <BadgeCheck className="h-10 w-10 text-gray-800" />
              <h2 
                className="text-center text-gray-800 text-2xl font-semibold"
                style={{ fontFamily: 'Calibri, Carlito, "Segoe UI", sans-serif' }}
              >
                Fitters for Mini Dealerships
              </h2>
            </div>
            
            <div className="space-y-4 max-w-3xl mx-auto">
              <p 
                className="text-center text-muted-foreground text-lg leading-relaxed"
                style={{ fontFamily: 'Calibri, Carlito, "Segoe UI", sans-serif' }}
              >
                We are a trusted supplier of graphics for Sytner Mini dealerships, delivering consistent quality, reliability, and a fully professional service. Our support includes both replacement graphics and fully custom-designed, in-house manufactured solutions tailored to dealership requirements.
              </p>
              <p 
                className="text-center text-muted-foreground text-lg leading-relaxed"
                style={{ fontFamily: 'Calibri, Carlito, "Segoe UI", sans-serif' }}
              >
                With a focus on precision, durability, and brand presentation, we ensure every graphic is produced and fitted to the highest standard—reinforcing the reputation and customer experience expected across the Sytner network.
              </p>
            </div>

            <div className="flex items-center justify-center gap-8 mt-8">
              <div className="flex items-center gap-2">
                <Award className="h-6 w-6 text-gray-700" />
                <span className="text-muted-foreground">OEM Quality</span>
              </div>
              <div className="flex items-center gap-2">
                <Shield className="h-6 w-6 text-gray-700" />
                <span className="text-muted-foreground">Trusted Partner</span>
              </div>
              <div className="flex items-center gap-2">
                <Star className="h-6 w-6 text-gray-700" />
                <span className="text-muted-foreground">Premium Finish</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GraphicsSupplyFitting;
